import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Box, Typography } from "@mui/material";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  border: "1px solid #E5E7EB",
  overflow: "hidden",
  "&::-webkit-scrollbar": {
    height: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#F3F4F6",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#D1D5DB",
    borderRadius: "4px",
    "&:hover": {
      background: "#9CA3AF",
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F8FAFC",
    color: "#374151",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    padding: "12px 16px",
    borderBottom: "2px solid #E5E7EB",
    textTransform: "none",
    letterSpacing: "0.025em",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "14px",
    lineHeight: "20px",
    color: "#1F2937",
    padding: "12px 16px",
    borderBottom: "1px solid #F3F4F6",
    verticalAlign: "middle",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#F9FAFB",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "#FAFAFA",
  },
  "&:nth-of-type(even):hover": {
    backgroundColor: "#F3F4F6",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 24px",
  textAlign: "center",
}));

const EmptyStateIcon = styled(Box)(({ theme }) => ({
  width: "64px",
  height: "64px",
  borderRadius: "50%",
  backgroundColor: "#F3F4F6",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "16px",
  "&::before": {
    content: '"📊"',
    fontSize: "24px",
  },
}));

function CustomizedTable({ rows, columns, tdRenderer, scrollable = false }) {
  function Row(props) {
    const { row, index } = props;

    return (
      <StyledTableRow key={`rowItem-${index}`} data-row-id={row?._id || row?.id}>
        {columns.map((columnItem, i) => {
          switch (columnItem["field"]) {
            default:
              return (
                <StyledTableCell align={columnItem.align} key={`columnItem-${i}`}>
                  {columnItem.rendererType
                    ? tdRenderer(row, columnItem, i)
                    : row[columnItem.field]}
                </StyledTableCell>
              );
          }
        })}
      </StyledTableRow>
    );
  }

  return (
    <StyledTableContainer
      sx={
        scrollable
          ? { width: "100%", overflowX: "auto", overflowY: "hidden" }
          : undefined
      }
    >
      <Table sx={{ minWidth: 700 }} aria-label="customized table">
        <TableHead>
          <TableRow>
            {columns.map(({ title, align, width }, index) => (
              <StyledTableCell
                sx={{ width }}
                align={align}
                key={`column-${index}`}
              >
                {title}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.length ? (
            rows.map((row, index) => (
              <Row index={index} row={row} key={`rowItem-${index}`} />
            ))
          ) : (
            <StyledTableRow>
              <StyledTableCell
                colSpan={columns.length}
                sx={{ borderBottom: 0, padding: 0 }}
              >
                <EmptyStateContainer>
                  <EmptyStateIcon />
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#6B7280",
                      fontWeight: 500,
                      marginBottom: "8px",
                    }}
                  >
                    No data available
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#9CA3AF",
                      maxWidth: "300px",
                    }}
                  >
                    There are no records to display at the moment. Check back later or try refreshing the page.
                  </Typography>
                </EmptyStateContainer>
              </StyledTableCell>
            </StyledTableRow>
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
}

export { CustomizedTable };
